import React, { useEffect, useState } from 'react'
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { toast } from 'react-toastify'
import { setStoreOwner, UnshippedOrdersError } from '../../firebase/shopify'

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`

/**
 * Assigns the marketplace account that a single Shopify store's revenue is
 * credited to.
 *
 * Nothing about the store's products or past orders is rewritten when this
 * changes -- they point at the store itself, so the incoming owner inherits
 * them by owning the store. That is also why the unshipped-order warning
 * matters: payout follows ownership at the moment delivery is confirmed.
 */
export default function DialogStoreOwner({ store, open, onClose, onSaved }) {
    const [ownerEmailInput, setOwnerEmailInput] = useState('')
    const [saving, setSaving] = useState(false)
    // Set when the server refuses an unconfirmed handover. Holding it here
    // turns the second click into an explicit acknowledgement, not a retry.
    const [pendingHandover, setPendingHandover] = useState(null)

    useEffect(() => {
        if (!open) return
        setOwnerEmailInput(store?.owner_email || '')
        setPendingHandover(null)
    }, [open, store])

    const save = async (confirmed) => {
        if (!ownerEmailInput.trim()) {
            toast.error('Enter an account email first.')
            return
        }

        setSaving(true)
        try {
            const info = await setStoreOwner(store.shop_domain, ownerEmailInput.trim(), { confirmed })
            toast.success(`${store.shop_domain} is now owned by ${info.owner_display_name}`)
            onSaved?.(info)
            onClose()
        } catch (error) {
            if (error instanceof UnshippedOrdersError) {
                setPendingHandover(error.handover)
                return
            }
            console.error('Failed to update store owner:', error)
            toast.error(error.message || 'Failed to update the store owner')
        } finally {
            setSaving(false)
        }
    }

    const outstanding = store?.handover

    return (
        <Dialog open={open} onClose={onClose} className="relative z-[10000]">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-black backdrop-blur-sm bg-opacity-75 transition-opacity"
            />
            <div className="fixed inset-0 flex items-center justify-center p-4">
                <DialogPanel className="w-full max-w-md rounded-[12px] bg-[#1F1F1F] border border-[#3A3A3A] p-6 shadow-xl">
                    <DialogTitle className="text-white text-lg mb-1">Store owner</DialogTitle>
                    <p className="text-sm text-[#a0a0a0] mb-1">{store?.shop_domain}</p>
                    <p className="text-sm text-[#a0a0a0] mb-4">
                        Sales from this store are credited to this account, the same way a regular seller's sales are.
                        The account must already exist.
                    </p>

                    <input
                        type="email"
                        value={ownerEmailInput}
                        onChange={(event) => setOwnerEmailInput(event.target.value)}
                        placeholder="owner@example.com"
                        className="w-full bg-[#141414] border border-[#3A3A3A] text-white rounded-[12px] px-3 py-2"
                    />

                    <p className="text-sm text-[#cfcfcf] mt-3">
                        Currently: <span className="text-white">{store?.owner_display_name || 'Unassigned'}</span>
                        {!store?.owner_uid && store?.owner_email && (
                            <span className="text-yellow-400"> (no matching account found)</span>
                        )}
                    </p>

                    {/* Standing notice, before the admin commits to anything.
                        The blocking confirmation below appears only on save. */}
                    {outstanding?.unshipped_orders > 0 && !pendingHandover && (
                        <p className="text-sm text-yellow-400 mt-3">
                            {store?.owner_display_name || 'The current owner'} has{' '}
                            {plural(outstanding.unshipped_orders, 'unshipped order')}
                            {outstanding.shipped_not_delivered > 0 &&
                                ` and ${plural(outstanding.shipped_not_delivered, 'order')} in transit`}.
                        </p>
                    )}

                    {pendingHandover && (
                        <div className="mt-4 rounded-[12px] border border-yellow-500/60 bg-yellow-500/10 p-4">
                            <p className="text-sm text-yellow-300 font-medium mb-2">
                                {plural(pendingHandover.unshipped_orders, 'unshipped order')} still outstanding
                            </p>
                            <p className="text-sm text-[#e6e6e6]">
                                The new owner inherits all {plural(pendingHandover.open_orders, 'open order')} on this
                                store, including any the current owner is already fulfilling, and will be paid for
                                them when each delivery is confirmed.
                            </p>
                            <p className="text-sm text-[#e6e6e6] mt-2">
                                Orders already delivered were paid out to the current owner and stay that way.
                            </p>
                        </div>
                    )}

                    <div className="flex gap-3 justify-end mt-6">
                        <button
                            onClick={onClose}
                            className="px-6 py-2 bg-[#141414] border border-[#3A3A3A] text-white rounded-[33px]"
                        >
                            Cancel
                        </button>
                        {pendingHandover ? (
                            <button
                                className="px-6 py-2 bg-yellow-400 text-black rounded-[33px] disabled:opacity-60"
                                onClick={() => save(true)}
                                disabled={saving}
                            >
                                {saving ? 'Reassigning...' : 'Reassign anyway'}
                            </button>
                        ) : (
                            <button
                                className="px-6 py-2 bg-white text-black rounded-[33px] disabled:opacity-60"
                                onClick={() => save(false)}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : 'Save'}
                            </button>
                        )}
                    </div>
                </DialogPanel>
            </div>
        </Dialog>
    )
}
